import { createSelector } from "reselect"
import { get, chain, map, filter, find, first } from "lodash"
import pluralize from "pluralize"
import { getTickerAndMicFromContributor } from "helpers/securities"
import { getAllAvailableRollups, getRollupSummaryLabel } from "helpers/rollups"
import { getScreenName } from "modules/analyze-forecast/selectors/file"
import { computeGroupedContributorsCSV, computeContributorsGroupCSV, computeContributorsCSV } from "helpers/csv"
import { getSelected } from "modules/analyze-forecast-details/selectors"
import { getPortfolioName } from "modules/portfolio-root/selectors/portfolios"
import { getTagCategories, getAttributeCategories } from "modules/portfolio-root/selectors"
import { computeSelectedGroup } from "helpers/analyze"
import { VIEW_TYPES } from "helpers/base"
import { computeSelectedGroupSiblings, computeSortedContributors, computeSortedGroupContributors } from "helpers/contributors"

const EMPTY_OBJECT = {}

const getContributorsData = state => get(state, "global.data.forecastContributors.data.model.simulation.forecastContributors.contributors")
const getGroupedContributorsData = state => get(state, "global.data.groupedForecastContributors.data.model.simulation.groupedForecastContributors.groupedContributors")
const getRawSelectedGroup = (state, props) => get(props, "selectedGroup", EMPTY_OBJECT)
const getContributorsGrouping = (state, props) => get(props, "contributorsGrouping")
const getOrder = state => get(state, "modules.analyzeForecastDetails.order", EMPTY_OBJECT)
const getClassifications = (state) => get(state, "global.data.classifications.data.classifications")
const getSelectedDate = (state, props) => props.selectedDate

const getSelectedField = createSelector(
  [getSelected],
  (selected) => get(selected, "field", "total"),
)

const getContributors = createSelector(
  [getContributorsData],
  (data) => {
    return map(data, security => {
      const { ticker, mic: exchange } = getTickerAndMicFromContributor(security)

      return {
        id: security.id,
        country: security.country,
        description: security.description,
        classification: get(security, "classification.name"),
        currency: security.currency,
        assetClass: security.assetClass,
        size: security.percentEquity,
        bloombergTicker: security.bloombergTicker,
        modelProviderId: security.modelProviderId,
        factors: {
          expectedReturn: security.weightedExpectedReturn / 100,
        },
        ticker,
        exchange,
      }
    })
  },
)

const getFilteredContributors = createSelector(
  [getContributors, getSelectedField],
  (data, selectedField) => {
    return filter(data, security => {
      return get(security, selectedField) != null
    })
  },
)

export const getSelectedGroup = createSelector(
  [getRawSelectedGroup, getGroupedContributorsData],
  computeSelectedGroup,
)

export const getSortedContributors = createSelector(
  [getFilteredContributors, getSelectedField, getOrder],
  computeSortedContributors,
)

const getGroupedContributors = createSelector(
  [getGroupedContributorsData],
  (data) => {
    return map(data, group => ({
      ...group,
      factors: {
        expectedReturn: group.totalExpectedReturn / 100,
      },
      contributors: map(group.contributors, security => {
        const { ticker, mic: exchange } = getTickerAndMicFromContributor(security)
        return {
          id: security.id,
          country: security.country,
          description: security.description,
          classification: get(security, "classification.name"),
          currency: security.currency,
          assetClass: security.assetClass,
          size: security.percentEquity,
          bloombergTicker: security.bloombergTicker,
          modelProviderId: security.modelProviderId,
          factors: {
            expectedReturn: security.weightedExpectedReturn / 100,
          },
          ticker,
          exchange,
        }
      }),
    }))
  },
)

const getFilteredGroupedContributors = createSelector(
  [getGroupedContributors, getSelectedField, getSelectedGroup],
  (data, selectedField, selectedGroup) => {
    return chain(data)
      .filter(group => !selectedGroup || selectedGroup.id === group.id)
      .map(group => ({
        ...group,
        contributors: filter(group.contributors, security => get(security, selectedField)),
      }))
      .value()
  },
)

export const getSortedGroupContributors = createSelector(
  [getFilteredGroupedContributors, getSelectedField, getOrder],
  computeSortedGroupContributors,
)

const getAccountRollups = createSelector(
  [getTagCategories, getClassifications, getAttributeCategories],
  (tagCategories, classifications, attributeCategories) => ({
    tagCategories,
    classifications,
    attributeCategories,
  }),
)
export const getAvailableRollups = createSelector(
  [getAccountRollups, getSelectedDate],
  getAllAvailableRollups,
)

export const getContributorsSummaryText = createSelector(
  [getContributorsGrouping, getSortedContributors, getSortedGroupContributors, getSelectedGroup],
  (contributorsGrouping, contributors, groups, selectedGroup) => {
    if (selectedGroup) {
      const numberOfSecurities = get(first(groups), "contributors.length", 0)
      return `${numberOfSecurities} ${pluralize("Security", numberOfSecurities)}`
    } else if (contributorsGrouping) {
      return `${groups.length} ${pluralize(getRollupSummaryLabel(contributorsGrouping), groups.length)}`
    } else {
      return `${contributors.length} ${pluralize("Security", contributors.length)}`
    }
  },
)

export const getCsvData = createSelector(
  [getSortedContributors, getSortedGroupContributors, getSelected, getContributorsGrouping, getPortfolioName, getSelectedDate, getSelectedGroup, getScreenName],
  (data, groupedData, selected, contributorsGrouping, portfolioName, selectedDate, selectedGroup, screenName) => {
    if (selectedGroup) {
      const group = first(groupedData)
      return computeGroupedContributorsCSV({
        type: screenName,
        portfolioName,
        selected,
        selectedDate,
        viewType: VIEW_TYPES.ABSOLUTE,
        group,
      })
    }

    if (contributorsGrouping) {
      return computeContributorsGroupCSV({
        type: screenName,
        portfolioName,
        selected,
        selectedDate,
        viewType: VIEW_TYPES.ABSOLUTE,
        contributorsGrouping,
        data: groupedData,
      })
    }

    return computeContributorsCSV({
      type: screenName,
      portfolioName,
      selected,
      selectedDate,
      viewType: VIEW_TYPES.ABSOLUTE,
      data,
    })
  },
)

export const getSelectedGroupValue = createSelector(
  [getSelectedGroup, getGroupedContributors, getSelectedField],
  (selectedGroup, groupedContributors, selectedField) => {
    if (selectedGroup) {
      const group = find(groupedContributors, { id: selectedGroup.id })
      return get(group, selectedField) * 100
    }

    return null
  },
)

export const getSelectedGroupSiblings = createSelector(
  [getSelectedGroup, getGroupedContributors, getSelectedField],
  computeSelectedGroupSiblings,
)
